import { useRef, useState } from 'react'
import { Settings, Upload, Loader2 } from 'lucide-react'
import { getBlockConfig } from './blocks/block-catalog'
import type { PageBlock } from '../../types'

function resizeImage(file: File, maxWidth: number, quality: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = document.createElement('img')
    const reader = new FileReader()
    reader.onload = (e) => {
      img.onload = () => {
        const scale = Math.min(1, maxWidth / img.width)
        const canvas = document.createElement('canvas')
        canvas.width = img.width * scale
        canvas.height = img.height * scale
        const ctx = canvas.getContext('2d')
        if (!ctx) { reject(new Error('Canvas not supported')); return }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
        resolve(canvas.toDataURL('image/jpeg', quality))
      }
      img.onerror = reject
      img.src = e.target?.result as string
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

function ImageUrlField({ value, onChange }: { value: string; onChange: (url: string) => void }) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 2 * 1024 * 1024) {
      alert('Image must be under 2MB')
      return
    }
    setUploading(true)
    try {
      const dataUrl = await resizeImage(file, 400, 0.8)
      onChange(dataUrl)
    } catch {
      alert('Failed to process image')
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  return (
    <div className="space-y-2">
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter image URL or upload below"
        className="w-full rounded-md border border-default-300 bg-content2 px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="flex items-center gap-1 px-2 py-1 text-xs border border-default-300 rounded-md hover:bg-content2 text-default-600 disabled:opacity-50"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
          {uploading ? 'Processing...' : 'Upload Image'}
        </button>
        {value && (
          <button type="button" className="text-xs text-danger hover:text-danger/80" onClick={() => onChange('')}>
            Clear
          </button>
        )}
      </div>
      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
      {value && <img src={value} alt="Preview" className="w-full h-20 object-contain border border-default-200 rounded" />}
    </div>
  )
}

function PropField({ propKey, value, onChange }: { propKey: string; value: string | number | boolean; onChange: (val: string | number | boolean) => void }) {
  const label = propKey.replace(/([A-Z])/g, ' $1').trim()

  if (propKey === 'imageUrl') {
    return (
      <label className="block">
        <span className="text-xs font-medium text-default-600 capitalize">{label}</span>
        <div className="mt-1"><ImageUrlField value={value as string} onChange={(url) => onChange(url)} /></div>
      </label>
    )
  }

  if (typeof value === 'boolean') {
    return (
      <label className="block">
        <span className="text-xs font-medium text-default-600 capitalize">{label}</span>
        <div className="mt-1">
          <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} className="rounded border-default-300 text-primary focus:ring-primary" />
        </div>
      </label>
    )
  }

  if (typeof value === 'number') {
    return (
      <label className="block">
        <span className="text-xs font-medium text-default-600 capitalize">{label}</span>
        <input type="number" value={value} onChange={(e) => onChange(Number(e.target.value))} className="mt-1 w-full rounded-md border border-default-300 bg-content2 px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" />
      </label>
    )
  }

  if (propKey.toLowerCase().includes('color')) {
    return (
      <label className="block">
        <span className="text-xs font-medium text-default-600 capitalize">{label}</span>
        <div className="mt-1 flex items-center gap-2">
          <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-8 h-8 rounded border border-default-300 cursor-pointer" />
          <input type="text" value={value} onChange={(e) => onChange(e.target.value)} className="flex-1 rounded-md border border-default-300 bg-content2 px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" />
        </div>
      </label>
    )
  }

  if ((value as string).length > 50) {
    return (
      <label className="block">
        <span className="text-xs font-medium text-default-600 capitalize">{label}</span>
        <textarea value={value as string} onChange={(e) => onChange(e.target.value)} rows={3} maxLength={2000} className="mt-1 w-full rounded-md border border-default-300 bg-content2 px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" />
      </label>
    )
  }

  return (
    <label className="block">
      <span className="text-xs font-medium text-default-600 capitalize">{label}</span>
      <input type="text" value={value as string} onChange={(e) => onChange(e.target.value)} maxLength={500} className="mt-1 w-full rounded-md border border-default-300 bg-content2 px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" />
    </label>
  )
}

interface PropertiesPanelProps {
  selectedBlock: PageBlock | null
  onUpdateProps: (blockId: string, props: Record<string, string | number | boolean>) => void
}

export function PropertiesPanel({ selectedBlock, onUpdateProps }: PropertiesPanelProps) {
  if (!selectedBlock) {
    return (
      <div className="w-56 border-l border-default-200 bg-content2 p-4 flex items-center justify-center">
        <p className="text-xs text-default-400 text-center">Select a block to edit its properties</p>
      </div>
    )
  }

  const config = getBlockConfig(selectedBlock.type)

  return (
    <div className="w-56 border-l border-default-200 bg-content2 overflow-y-auto p-3">
      <h3 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-3">
        <Settings className="w-4 h-4" />
        {config?.label} Settings
      </h3>
      <div className="space-y-3">
        {Object.entries(selectedBlock.props).map(([key, value]) => (
          <PropField
            key={key}
            propKey={key}
            value={value}
            onChange={(val) => onUpdateProps(selectedBlock.id, { ...selectedBlock.props, [key]: val })}
          />
        ))}
      </div>
    </div>
  )
}
